const chapter = {
  id: 'chemical-bonding',
  slug: 'chemical-bonding',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Chemical Bonding & Molecular Structure',
        hindi: 'रासायनिक बन्धन और आणविक संरचना',
  },
  estReadMinutes: 75,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3–4 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 3-4 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'kossel-lewis',
      title: {
        english: 'Kössel–Lewis Approach & Octet Rule',
        hindi: 'कोसेल-लुईस उपागम और अष्टक नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Atoms combine to attain a stable noble-gas configuration (octet for most main-group elements; duet for H, He).',
        hindi: 'परमाणु एक स्थिर नोबल-गैस विन्यास प्राप्त करने के लिए संयोजित होते हैं (अधिकांश मुख्य-समूह तत्वों के लिए ऑक्टेट; एच, हे के लिए युगल)।',
            },
            {
              english: '**Ionic bond** (Kössel): transfer of e⁻ between metal (low IE) and non-metal (high EA), e.g. Na⁺Cl⁻.',
        hindi: '**आयनिक बंधन** (कोसेल): धातु (कम IE) और गैर-धातु (उच्च EA) के बीच e⁻ का स्थानांतरण, उदाहरण के लिए। Na⁺Cl⁻.',
            },
            {
              english: '**Covalent bond** (Lewis): mutual sharing of one or more electron pairs, e.g. Cl–Cl, H–O–H.',
        hindi: '**सहसंयोजक बंधन** (लुईस): एक या अधिक इलेक्ट्रॉन युग्मों का पारस्परिक साझाकरण, उदाहरणार्थ। सीएल-सीएल, एच-ओ-एच।',
            },
            {
              english: '**Coordinate (dative)** bond: shared pair contributed entirely by one atom (donor); shown by →. e.g. NH₃→BF₃, NH₄⁺.',
        hindi: '**समन्वय (गोताखोर)** बंधन: साझा जोड़ी पूरी तरह से एक परमाणु (दाता) द्वारा योगदान दिया; द्वारा दिखाया गया →. जैसे NH₃→BF₃, NH₄⁺.',
            },
            {
              english: '**Formal charge** on atom $= V - L - B/2$ where $V$ = valence e⁻, $L$ = lone pair e⁻, $B$ = bonding e⁻.',
        hindi: '**परमाणु $= V - L - B/2$ पर औपचारिक चार्ज** जहां $V$ = संयोजकता e⁻, $L$ = अकेला जोड़ा e⁻, $B$ = बंधन e⁻।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: '**Exceptions to octet rule**: incomplete (BF₃, BeCl₂); expanded (PCl₅, SF₆, IF₇); odd-electron (NO, NO₂, ClO₂).',
        hindi: '**ऑक्टेट नियम के अपवाद**: अपूर्ण (BF₃, BeCl₂); विस्तारित (PCl₅, SF₆, IF₇); विषम-इलेक्ट्रॉन (NO, NO₂, ClO₂).',
          },
        },
      ],
    },
    {
      id: 'ionic-properties',
      title: {
        english: 'Ionic Bond — Lattice Energy, Fajans\' Rules',
        hindi: 'आयनिक बॉन्ड - Common Trapी ऊर्जा, फ़ैजंस नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Born–Landé / Lattice energy',
        hindi: 'बॉर्न-लैंडे / लैटिस ऊर्जा',
          },
          statement: {
            english: '$U \\propto \\dfrac{Z^+ \\cdot Z^-}{r^+ + r^-}$. Higher charges and smaller radii ⇒ stronger lattice ⇒ higher melting point, lower solubility in non-polar solvents.',
        hindi: '$U \\propto \\dfrac{Z^+ \\cdot Z^-}{r^+ + r^-}$। उच्च आवेश और छोटी त्रिज्या ⇒ मजबूत Common Trapी ⇒ उच्च गलनांक, गैर-ध्रुवीय सॉल्वैंट्स में कम घुलनशीलता। **बॉर्न-हैबर चक्र**: उर्ध्वपातन, IE, EA, पृथक्करण और गठन एन्थैल्पी का उपयोग करके $U \\propto \\dfrac{Z^+ \\cdot Z^-}{r^+ + r^-}$ का थर्मोडायनामिक निर्धारण।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Born–Haber cycle**: thermodynamic determination of $U$ using sublimation, IE, EA, dissociation and formation enthalpies.',
        hindi: '**बॉर्न-हैबर चक्र**: उर्ध्वपातन, IE, EA, पृथक्करण और गठन एन्थैल्पी का उपयोग करके $U$ का थर्मोडायनामिक निर्धारण।',
            },
            {
              english: '**Fajans\' rules** — covalent character in ionic bonds increases with:',
        hindi: '**फैजंस नियम** - आयनिक बंधों में सहसंयोजक चरित्र बढ़ता है:',
            },
            {
              english: '— small cation (high charge density) and large anion;',
        hindi: '- छोटा धनायन (उच्च चार्ज घनत्व) और बड़ा आयन;',
            },
            {
              english: '— high charge on either ion;',
        hindi: '- किसी भी आयन पर उच्च चार्ज;',
            },
            {
              english: '— pseudo-noble-gas cation configuration ($d^{10}$, e.g. Cu⁺, Ag⁺) is more polarising than noble-gas-type.',
        hindi: '- छद्म-नोबल-गैस धनायन विन्यास ($d^{10}$, उदाहरण के लिए Cu⁺, Ag⁺) उत्कृष्ट-गैस-प्रकार की तुलना में अधिक ध्रुवीकरण करने वाला है।',
            },
            {
              english: 'Effect: more covalent ⇒ lower melting point, lower solubility in water, more colour. e.g. AgCl < AgBr < AgI in ionic character.',
        hindi: 'प्रभाव: अधिक सहसंयोजक ⇒ कम गलनांक, पानी में कम घुलनशीलता, अधिक रंग। जैसे AgCl < AgBr < AgI आयनिक वर्ण में। **VBT** (हेइटलर-लंदन, पॉलिंग-स्लेटर): एक सहसंयोजक बंधन विपरीत स्पिन वाले दो परमाणुओं के एकल-कब्जे वाले परमाणु कक्षाओं के ओवरलैप द्वारा बनता है। ग्रेटर ओवरलैप ⇒ मजबूत बंधन।',
            },
          ],
        },
      ],
    },
    {
      id: 'vbt',
      title: {
        english: 'Valence Bond Theory & Hybridisation',
        hindi: 'संयोजकता बन्ध सिद्धान्त और संकरण',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: '**VBT** (Heitler–London, Pauling–Slater): a covalent bond forms by overlap of singly-occupied atomic orbitals of two atoms with opposite spins. Greater overlap ⇒ stronger bond.',
        hindi: '**वीबीटी** (हेइटलर-लंदन, पॉलिंग-स्लेटर): एक सहसंयोजक बंधन विपरीत स्पिन वाले दो परमाणुओं के एकल-कब्जे वाले परमाणु कक्षाओं के ओवरलैप द्वारा बनता है। ग्रेटर ओवरलैप ⇒ मजबूत बंधन।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$\\sigma$-bond: head-on overlap (s-s, s-p, p-p). Stronger.',
        hindi: '$\\sigma$-बॉन्ड: हेड-ऑन ओवरलैप (एस-एस, एस-पी, पी-पी)। मजबूत.',
            },
            {
              english: '$\\pi$-bond: sideways overlap of p-p (or p-d). Weaker; restricts rotation.',
        hindi: '$\\pi$-बॉन्ड: पीपी (या पी-डी) का पार्श्व ओवरलैप। कमज़ोर; घूर्णन को प्रतिबंधित करता है।',
            },
            {
              english: 'Bond order: single ($\\sigma$ only); double ($\\sigma + \\pi$); triple ($\\sigma + 2\\pi$).',
        hindi: 'बांड ऑर्डर: एकल ($\\sigma$ केवल); डबल ($\\sigma + \\pi$); ट्रिपल ($\\sigma + 2\\pi$).',
            },
            {
              english: 'Bond length: triple < double < single. Bond energy: triple > double > single.',
        hindi: 'बॉन्ड की लंबाई: ट्रिपल < डबल < सिंगल। बांड ऊर्जा: ट्रिपल > डबल > सिंगल।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Hybridisation',
        hindi: 'संकरण',
          },
          body: {
            english: 'Mixing of atomic orbitals of similar energies on the same atom to form an equal number of equivalent **hybrid orbitals** with directed geometry, prior to bond formation.',
        hindi: 'बंधन निर्माण से पहले, निर्देशित ज्यामिति के साथ समान संख्या में समतुल्य **हाइब्रिड ऑर्बिटल्स** बनाने के लिए एक ही परमाणु पर समान ऊर्जा वाले परमाणु ऑर्बिटल्स का मिश्रण।',
          },
        },
        {
          type: 'table',
          headers: ['Hybridisation', 'Geometry', 'Bond angle', 'Example'],
          rows: [
            ['$sp$', 'Linear', '$180°$', 'BeCl₂, CO₂, C₂H₂'],
            ['$sp^2$', 'Trigonal planar', '$120°$', 'BF₃, BCl₃, C₂H₄, NO₃⁻'],
            ['$sp^3$', 'Tetrahedral', '$109.5°$', 'CH₄, NH₄⁺, SO₄²⁻'],
            ['$sp^3 d$', 'Trig. bipyramidal', '$120°/90°$', 'PCl₅, PF₅'],
            ['$sp^3 d^2$', 'Octahedral', '$90°$', 'SF₆, [Co(NH₃)₆]³⁺'],
            ['$sp^3 d^3$', 'Pent. bipyramidal', '$72°/90°$', 'IF₇'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Quick formula for hybridisation of central atom: $H = \\tfrac12 (V + M - C + A)$ where $V$ = valence e⁻ on central, $M$ = monovalent atoms attached, $C$ = cation charge, $A$ = anion charge. $H = 2\\to sp,\\ 3\\to sp^2,\\ 4\\to sp^3,\\ 5\\to sp^3 d,\\ 6\\to sp^3 d^2$.',
        hindi: 'केंद्रीय परमाणु के संकरण के लिए त्वरित सूत्र: $H = \\tfrac12 (V + M - C + A)$ जहां $V$ = केंद्रीय पर वैलेंस e⁻, $M$ = मोनोवैलेंट परमाणु जुड़े हुए हैं, $C$ = धनायन चार्ज, $A$ = आयन चार्ज। $H = 2\\to sp,\\\\ 3\\to sp^2,\\\\ 4\\to sp^3,\\\\ 5\\to sp^3 d,\\\\ 6\\to sp^3 d^2$.',
          },
        },
      ],
    },
    {
      id: 'vsepr',
      title: {
        english: 'VSEPR Theory & Molecular Geometry',
        hindi: 'VSEPR सिद्धान्त और आणविक ज्यामिति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Electron pairs around the central atom (bond pairs **bp** + lone pairs **lp**) repel and arrange to maximise distance.',
        hindi: 'केंद्रीय परमाणु के चारों ओर इलेक्ट्रॉन जोड़े (बंध जोड़े **बीपी** + अकेले जोड़े **एलपी**) प्रतिकर्षित करते हैं और दूरी को अधिकतम करने की व्यवस्था करते हैं।',
            },
            {
              english: 'Repulsion order: **lp–lp > lp–bp > bp–bp**.',
        hindi: 'प्रतिकर्षण क्रम: **एलपी–एलपी > एलपी–बीपी > बीपी–बीपी**।',
            },
            {
              english: 'Lone pairs distort ideal geometry (compress bond angles).',
        hindi: 'एकाकी जोड़े आदर्श ज्यामिति (संपीड़ित बंधन कोण) को विकृत करते हैं।',
            },
            {
              english: 'Multiple bonds repel more than single bonds.',
        hindi: 'एकाधिक बांड एकल बांड की तुलना में अधिक प्रतिकर्षित करते हैं।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Steric no. (bp+lp)', 'lp', 'Geometry / Shape', 'Example'],
          rows: [
            ['2 (2,0)', '0', 'Linear', 'CO₂'],
            ['3 (3,0)', '0', 'Trigonal planar', 'BF₃'],
            ['3 (2,1)', '1', 'Bent', 'SO₂, O₃'],
            ['4 (4,0)', '0', 'Tetrahedral', 'CH₄'],
            ['4 (3,1)', '1', 'Pyramidal', 'NH₃ (107°)'],
            ['4 (2,2)', '2', 'Bent', 'H₂O (104.5°)'],
            ['5 (5,0)', '0', 'Trigonal bipyramidal', 'PCl₅'],
            ['5 (4,1)', '1', 'See-saw', 'SF₄'],
            ['5 (3,2)', '2', 'T-shape', 'ClF₃'],
            ['5 (2,3)', '3', 'Linear', 'XeF₂, I₃⁻'],
            ['6 (6,0)', '0', 'Octahedral', 'SF₆'],
            ['6 (5,1)', '1', 'Square pyramidal', 'BrF₅'],
            ['6 (4,2)', '2', 'Square planar', 'XeF₄'],
          ],
        },
      ],
    },
    {
      id: 'mot',
      title: {
        english: 'Molecular Orbital Theory (Diatomics)',
        hindi: 'आणविक कक्षक सिद्धान्त (द्विपरमाणुक)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Atomic orbitals combine into **bonding** (constructive) and **antibonding** (destructive, shown by *) MOs spread over both nuclei.',
        hindi: 'परमाणु कक्षाएँ दोनों नाभिकों में फैले **बॉन्डिंग** (रचनात्मक) और **एंटीबॉन्डिंग** (विनाशकारी, * द्वारा दिखाए गए) एमओ में संयोजित होती हैं।',
            },
            {
              english: 'For homonuclear diatomics of $Z \\le 7$ (Li₂ → N₂): order is $\\sigma 1s, \\sigma^*1s, \\sigma 2s, \\sigma^*2s, \\pi 2p_x = \\pi 2p_y, \\sigma 2p_z, \\pi^*2p_x = \\pi^*2p_y, \\sigma^*2p_z$.',
        hindi: '$Z \\le 7$ (Li₂ → N₂) के होमोन्यूक्लियर डायटोमिक्स के लिए: क्रम $\\sigma 1s, \\sigma^*1s, \\sigma 2s, \\sigma^*2s, \\pi 2p_x = \\pi 2p_y, \\sigma 2p_z, \\pi^*2p_x = \\pi^*2p_y, \\sigma^*2p_z$ है।',
            },
            {
              english: 'For O₂, F₂, Ne₂: $\\sigma 2p_z$ comes **before** the $\\pi 2p$ pair.',
        hindi: 'O₂, F₂, Ne₂ के लिए: $\\sigma 2p_z$ $\\pi 2p$ जोड़ी से **पहले** आता है।',
            },
            {
              english: '**Bond order** (BO) $= (N_b - N_a)/2$. BO > 0 ⇒ molecule exists; higher BO ⇒ shorter, stronger bond.',
        hindi: '**बॉन्ड ऑर्डर** (बीओ) $= (N_b - N_a)/2$। बीओ > 0 ⇒ अणु मौजूद है; उच्चतर बीओ ⇒ छोटा, मजबूत बंधन।',
            },
            {
              english: 'Magnetic behaviour: any unpaired e⁻ ⇒ **paramagnetic** (e.g. O₂); all paired ⇒ **diamagnetic** (e.g. N₂, F₂).',
        hindi: 'चुंबकीय व्यवहार: कोई भी अयुग्मित e⇒ **प्रतिचुंबकीय** (जैसे O₂); सभी युग्मित ⇒ **डायमैग्नेटिक** (जैसे N₂, F₂)।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Bond orders and magnetic nature of O₂, O₂⁺, O₂⁻.',
        hindi: 'बॉन्ड ऑर्डर और O₂, O₂⁺, O₂⁻ की चुंबकीय प्रकृति।',
          },
          solution: {
            english: `O₂: 16 e⁻, BO = (10−6)/2 = 2, **paramagnetic** (2 unpaired in $\\pi^*$).
O₂⁺: 15 e⁻, BO = (10−5)/2 = 2.5, paramagnetic.
O₂⁻: 17 e⁻, BO = (10−7)/2 = 1.5, paramagnetic.`,
            hindi: 'O₂: 16 e⁻, BO = (10−6)/2 = 2, **पैरामैग्नेटिक** ($\\pi^*$ में 2 अयुग्मित)। O₂⁺: 15 e⁻, BO = (10−5)/2 = 2.5, अनुचुम्बकीय। O₂⁻: 17 e⁻, BO = (10−7)/2 = 1.5, अनुचुम्बकीय।',
          },
        },
      ],
    },
    {
      id: 'polarity-hbond',
      title: {
        english: 'Polarity, Dipole Moment & Hydrogen Bonding',
        hindi: 'ध्रुवता, द्विध्रुव आघूर्ण और हाइड्रोजन बन्धन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Dipole moment**: $\\vec\\mu = q \\cdot \\vec d$. Unit: Debye (1 D = $3.33\\times 10^{-30}$ C·m).',
        hindi: '**द्विध्रुव क्षण**: $\\vec\\mu = q \\cdot \\vec d$. यूनिट: डेबाई (1 डी = $3.33\\times 10^{-30}$ C·m)।',
            },
            {
              english: 'Vector sum of bond moments. Symmetric molecules (CO₂, BF₃, CCl₄, SF₆) have $\\mu = 0$.',
        hindi: 'बंधन क्षणों का सदिश योग. सममित अणुओं (CO₂, BF₃, CCl₄, SF₆) में $\\mu = 0$ होता है।',
            },
            {
              english: 'NH₃ (1.46 D) > NF₃ (0.24 D): bond dipoles add in NH₃ but partially cancel in NF₃.',
        hindi: 'NH₃ (1.46 डी) > एनएफ₃ (0.24 डी): बांड द्विध्रुव एनएच₃ में जुड़ते हैं लेकिन एनएफ₃ में आंशिक रूप से रद्द हो जाते हैं।',
            },
            {
              english: 'H₂O > H₂S: O is more electronegative; also bent geometry adds vectorially.',
        hindi: 'H₂O > H₂S: O अधिक विद्युत ऋणात्मक है; बेंट ज्योमेट्री भी सदिश रूप से जुड़ती है।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Hydrogen bond',
        hindi: 'हाइड्रोजन बंधन',
          },
          body: {
            english: 'Electrostatic attraction between H atom covalently bonded to highly electronegative atom (N, O, F) and a lone pair on another electronegative atom. Strength: 5–40 kJ/mol (weaker than covalent, much stronger than van der Waals).',
        hindi: 'उच्च विद्युत ऋणात्मक परमाणु (एन, ओ, एफ) से सहसंयोजक रूप से बंधे एच परमाणु और दूसरे विद्युत ऋणात्मक परमाणु पर एक अकेले जोड़े के बीच इलेक्ट्रोस्टैटिक आकर्षण। ताकत: 5-40 kJ/mol (सहसंयोजक से कमजोर, वैन डेर वाल्स से काफी मजबूत)। **अंतरआण्विक** एच-बंधन (अणुओं के बीच) ⇒ उच्च kJ/mol बिंदु, चिपचिपाहट (H₂O, HF, NH₃, अल्कोहल, कार्बोक्जिलिक एसिड)। **इंट्रामोलेक्यूलर** H-बॉन्ड (समान अणु के भीतर) ⇒ अपेक्षा से कम kJ/mol (उदाहरण के लिए *o*-नाइट्रोफेनॉल बनाम *p*-नाइट्रोफेनॉल)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Intermolecular** H-bond (between molecules) ⇒ high boiling/melting point, viscosity (H₂O, HF, NH₃, alcohols, carboxylic acids).',
        hindi: '**अंतरआण्विक** एच-बंध (अणुओं के बीच) ⇒ उच्च boiling/melting बिंदु, चिपचिपाहट (H₂O, HF, NH₃, अल्कोहल, कार्बोक्जिलिक एसिड)।',
            },
            {
              english: '**Intramolecular** H-bond (within same molecule) ⇒ lower bp/mp than expected (e.g. *o*-nitrophenol vs *p*-nitrophenol).',
        hindi: '**इंट्रामोलेक्यूलर** एच-बॉन्ड (एक ही अणु के भीतर) ⇒ अपेक्षा से कम bp/mp (उदाहरण के लिए *o*-नाइट्रोफेनॉल बनाम *p*-नाइट्रोफेनॉल)।',
            },
            {
              english: 'Anomalously high bp of H₂O (vs H₂S) and HF (vs HCl) due to H-bonding.',
        hindi: 'H-बॉन्डिंग के कारण H₂O (बनाम H₂S) और HF (बनाम HCl) का असामान्य रूप से उच्च बीपी।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'U \\propto Z^+ Z^-/(r^+ + r^-)',
      desc: {
        english: 'Lattice energy',
        hindi: 'Common Trapक ऊर्जा',
      },
    },
    {
      latex: 'FC = V - L - B/2',
      desc: {
        english: 'Formal charge',
        hindi: 'औपचारिक शुल्क',
      },
    },
    {
      latex: 'H = \\tfrac12(V + M - C + A)',
      desc: {
        english: 'Hybridisation count',
        hindi: 'संकरण गणना',
      },
    },
    {
      latex: 'BO = (N_b - N_a)/2',
      desc: {
        english: 'Bond order (MOT)',
        hindi: 'बांड ऑर्डर (एमओटी)',
      },
    },
    {
      latex: '\\vec\\mu = q\\,\\vec d',
      desc: {
        english: 'Dipole moment',
        hindi: 'द्विध्रुव आघूर्ण',
      },
    },
    {
      latex: '\\text{Repulsion: lp-lp > lp-bp > bp-bp}',
      desc: {
        english: 'VSEPR rule',
        hindi: 'वीएसईपीआर नियम',
      },
    },
    {
      latex: '1\\ \\text{D} = 3.33\\times 10^{-30}\\ \\text{C·m}',
      desc: {
        english: 'Debye',
        hindi: 'डिबाई',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Hybridisation',
      problem: {
        english: 'Hybridisation of central atom in XeF₄?',
        hindi: 'XeF₄ में केंद्रीय परमाणु का संकरण?',
      },
      answer: {
        english: '$sp^3 d^2$',
        hindi: '$sp^3 d^2$',
      },
      solution: {
        english: 'Xe has 8 v.e.; 4 bond pairs + 2 lone pairs ⇒ steric no. 6 ⇒ square planar.',
        hindi: 'Xe में 8 v.e. हैं; 4 बांड जोड़े + 2 एकाकी जोड़े ⇒ स्टेरिक नं. 6 ⇒ वर्ग समतल.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'MOT',
      problem: {
        english: 'Bond order of O₂⁺ ?',
        hindi: 'O₂⁺ का बांड ऑर्डर?',
      },
      answer: {
        english: '$2.5$',
        hindi: '$2.5$',
      },
      solution: {
        english: '15 e⁻; $(10−5)/2 = 2.5$.',
        hindi: '15 ई⁻; $(10−5)/2 = 2.5$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'VSEPR',
      problem: {
        english: 'Shape of SF₄?',
        hindi: 'SF₄ का आकार?',
      },
      answer: {
        english: 'See-saw',
        hindi: 'सी-सॉ',
      },
      solution: {
        english: 'Steric no. 5 with 1 lone pair.',
        hindi: 'स्टेरिक नं. 1 अकेले जोड़े के साथ 5.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Dipole',
      problem: {
        english: 'Which has zero dipole moment: H₂O, NH₃, CO₂, SO₂?',
        hindi: 'किसका द्विध्रुव आघूर्ण शून्य है: H₂O, NH₃, CO₂, SO₂?',
      },
      answer: {
        english: 'CO₂',
        hindi: 'CO₂',
      },
      solution: {
        english: 'Linear and symmetric — bond dipoles cancel.',
        hindi: 'रैखिक और सममित - बंधन द्विध्रुव रद्द हो जाते हैं।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'H-bond',
      problem: {
        english: 'Boiling point order of HF, HCl, HBr, HI?',
        hindi: 'HF, HCl, HBr, HI का क्वथनांक क्रम?',
      },
      answer: {
        english: 'HCl < HBr < HI < HF',
        hindi: 'एचसीएल <एचबीआर <एचआई <एचएफ',
      },
      solution: {
        english: 'HF abnormally high due to H-bonding; rest follow vdW (size).',
        hindi: 'एच-बॉन्डिंग के कारण एचएफ असामान्य रूप से उच्च है; बाकी vdW (आकार) का पालन करें।',
      },
    },
  ],
  next: {
    slug: 'states-of-matter',
    title: 'States of Matter — Gases & Liquids',
  },
};

export default chapter;
